import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Filter, Eye, Edit, Trash2, UserCheck, UserX, Phone } from 'lucide-react';
import Button from '../components/UI/Button';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { User } from '../types';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  assignedTours: number[];
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tours, setTours] = useState<Array<{ id: number; title: string; name: string; place?: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterNameInput, setFilterNameInput] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'blocked'>('all');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [sortColumn, setSortColumn] = useState<keyof User>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [totalUsers, setTotalUsers] = useState(0);

  // Tour infinite scroll pagination
  const [tourPage, setTourPage] = useState(1);
  const [tourTotalPages, setTourTotalPages] = useState(1);
  const [isLoadingMoreTours, setIsLoadingMoreTours] = useState(false);
  const editTourListRef = useRef<HTMLDivElement>(null);
  const createTourListRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UserFormData>();

  const watchedEmail = watch('email');
  const watchedPhone = watch('phone');
  const watchedAssignedTours = watch('assignedTours') || [];

  // Handle tour assignment checkbox changes
  const handleTourToggle = (tourId: number, checked: boolean) => {
    const currentTours = watchedAssignedTours;
    if (checked) {
      // Add tour if not already in the list
      if (!currentTours?.includes(tourId)) {
        setValue('assignedTours', [...currentTours, tourId]);
      }
    } else {
      // Remove tour from the list
      setValue('assignedTours', currentTours?.filter(id => id !== tourId));
    }
  };

  // Debounce the name search input — only hit the API after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterName(filterNameInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterNameInput]);

  // Fetch users from backend with search + status + pagination
  const fetchUsers = async (page: number = 1, search = filterName, status = filterStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      const flavourId = apiService.getFlavourId();
      if (!flavourId) {
        setUsers([]);
        setTotalUsers(0);
        setIsLoading(false);
        return;
      }
      const response = await apiService.getUsers(page, rowsPerPage, search || undefined, status);
      setUsers(response.users || []);
      setTotalUsers(response.total || 0);
      setCurrentPage(response.page || page);
    } catch (error: any) {
      setError('Failed to load users');
      toast.error(error?.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch first page of tours for assignment
  const fetchTours = async () => {
    try {
      const response = await apiService.getToursForAssignment(1, 15);
      setTours(response.tours || []);
      setTourPage(1);
      setTourTotalPages(response.totalPages ?? 1);
    } catch (error) {
      // ignore tour load failure
    }
  };

  useEffect(() => {
    fetchTours();
  }, []);

  // Load next page of tours and append
  const fetchMoreTours = useCallback(async () => {
    const nextPage = tourPage + 1;
    if (isLoadingMoreTours || nextPage > tourTotalPages) return;
    setIsLoadingMoreTours(true);
    try {
      const response = await apiService.getToursForAssignment(nextPage, 15);
      setTours(prev => [...prev, ...(response.tours || [])]);
      setTourPage(nextPage);
    } catch (error) {
      // ignore tour load failure
    } finally {
      setIsLoadingMoreTours(false);
    }
  }, [tourPage, tourTotalPages, isLoadingMoreTours]);

  // Scroll handler for tour list containers
  const handleTourListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) {
      fetchMoreTours();
    }
  }, [fetchMoreTours]);

  // Re-fetch from page 1 whenever debounced search or status changes
  useEffect(() => {
    setCurrentPage(1);
    fetchUsers(1, filterName, filterStatus);
  }, [filterName, filterStatus]);

  // Sort users (server already handles filtering; client only sorts current page)
  const sortedUsers = [...users].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    
    if (aValue === undefined || bValue === undefined) return 0;
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const handleSort = (column: keyof User) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleView = (user: User) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setValue('firstName', user.firstName || '');
    setValue('lastName', user.lastName || '');
    setValue('email', user.email);
    setValue('phone', user.phone || '');
    setValue('assignedTours', user.assignedTours || []);
    setIsEditModalOpen(true);
  };

  const handleCreate = () => {
    reset();
    setValue('assignedTours', []);
    setIsCreateModalOpen(true);
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      toast.success('User deleted successfully!');
      await fetchUsers(currentPage);
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleBlockUser = async (userId: number, isBlocked: boolean) => {
    try {
      await apiService.updateUserStatus(userId, { isBlocked: !isBlocked });
      toast.success(`User ${isBlocked ? 'unblocked' : 'blocked'} successfully!`);
      await fetchUsers(currentPage);
    } catch (error) {
      toast.error(`Failed to ${isBlocked ? 'unblock' : 'block'} user`);
    }
  };

  const onSubmitEdit = async (data: UserFormData) => {
    if (!selectedUser) return;
    
    setIsSubmitting(true);
    try {
      await apiService.updateUser(selectedUser.id, data);
      toast.success('User updated successfully!');
      setIsEditModalOpen(false);
      setSelectedUser(null);
      reset();
      await fetchUsers(currentPage);
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitCreate = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      await apiService.createUser(data);
      toast.success('User created successfully!');
      setIsCreateModalOpen(false);
      reset();
      await fetchUsers(1);
    } catch (error) {
      toast.error('Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalUsers / rowsPerPage));

  const columns = [
    {
      key: 'name' as keyof User,
      header: 'Name',
      sortable: true,
      render: (_value: any, user: User) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-sm font-semibold text-white">
              {user.firstName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 max-w-[200px] truncate">
              {user?.firstName && user?.lastName
                ? `${user?.firstName} ${user?.lastName}`
                : user?.firstName || 'N/A'
              }
            </p>
            <p className="text-sm text-gray-500 max-w-[200px] truncate">{user?.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'phone' as keyof User,
      header: 'Phone',
      sortable: true,
      render: (_value: any, user: User) => (
        <div className="flex items-center space-x-2">
          <Phone className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-900">{user?.phone || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'status' as keyof User,
      header: 'Status',
      sortable: false,
      render: (_value: any, user: User) => (
        <div className="flex items-center space-x-2">
          {user.isBlocked ? (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
              <UserX className="h-3 w-3 mr-1" />
              Blocked
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              <UserCheck className="h-3 w-3 mr-1" />
              Active
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt' as keyof User,
      header: 'Joined',
      sortable: true,
      render: (_value: any, user: User) => (
        <span className="text-sm text-gray-500">
          {user?.createdAt ? new Date(user?.createdAt).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
    {
      key: 'assignedTours' as keyof User,
      header: 'Assigned Tours',
      sortable: false,
      render: (_value: any, user: User) => (
        <div className="flex flex-wrap gap-1">
          {user?.assignedTours && user?.assignedTours.length > 0 ? (
            user?.assignedTours.slice(0, 2).map((tourId) => {
              const tour = tours.find(t => t.id === tourId);
              return tour ? (
                <span
                  key={tourId}
                  title={tour.title}
                  className="inline-block max-w-[160px] min-w-0 truncate align-middle px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                >
                  {tour.title}
                </span>
              ) : null;
            }).concat(
              user?.assignedTours.length > 2 ? [
                <span
                  key="more"
                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600"
                >
                  +{user?.assignedTours.length - 2} more
                </span>
              ] : []
            )
          ) : (
            <span className="text-sm text-gray-400">No tours assigned</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions' as keyof User,
      header: 'Actions',
      sortable: false,
      render: (_value: any, user: User) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleView(user)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View user details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEdit(user)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Edit user"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(user.id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete user"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => fetchUsers()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Users Management</h1>
          <p className="text-gray-600">Manage end users who use your Trippy tour applications</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={handleCreate} disabled={!apiService.getFlavourId()} title={!apiService.getFlavourId() ? 'Please create an app first' : ''}>
            Add User
          </Button>
          <Button variant="outline" onClick={() => window.open('/admin-users', '_blank')}>
            Manage Admin Users
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filterNameInput}
              onChange={(e) => setFilterNameInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          <div className="flex items-center justify-end">
            <Button onClick={() => fetchUsers(1)} variant="outline">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <Table
        data={sortedUsers}
        columns={columns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        loading={isLoading}
        emptyMessage="No users found"
      />

      {/* Pagination Controls */}
      {sortedUsers.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => fetchUsers(currentPage - 1)} 
            >
              Previous
            </Button>

            <Button
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => fetchUsers(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* View User Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedUser(null);
        }}
        title="User Details"
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-xl font-semibold text-white">
                  {selectedUser.firstName?.charAt(0) || selectedUser.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedUser.firstName && selectedUser.lastName 
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : selectedUser.firstName || 'N/A'
                  }
                </h3>
                <p className="text-gray-600">{selectedUser.email}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="text-sm text-gray-900">{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <div className="mt-1">
                  {selectedUser.isBlocked ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                      <UserX className="h-3 w-3 mr-1" />
                      Blocked
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      <UserCheck className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Joined</label>
                <p className="text-sm text-gray-900">
                  {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Platform</label>
                <p className="text-sm text-gray-900">
                  {selectedUser.platform === 'ios'
                    ? 'iOS'
                    : selectedUser.platform === 'android'
                    ? 'Android'
                    : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Language</label>
                <p className="text-sm text-gray-900">{selectedUser.language ? selectedUser.language.toUpperCase() : 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">App Store Region</label>
                <p className="text-sm text-gray-900">{selectedUser.storeRegion || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Assigned Tours</label>
                <div className="mt-1">
                  {selectedUser.assignedTours && selectedUser.assignedTours.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedUser.assignedTours.map((tourId) => {
                        const tour = tours.find(t => t.id === tourId);
                        return tour ? (
                          <span
                            key={tourId}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                          >
                            {tour.title}
                          </span>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No tours assigned</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsViewModalOpen(false);
                  handleEdit(selectedUser);
                }}
              >
                Edit User
              </Button>
              <Button
                variant="outline"
                onClick={() => handleBlockUser(selectedUser.id, selectedUser.isBlocked)}
              >
                {selectedUser.isBlocked ? 'Unblock User' : 'Block User'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
          reset();
        }}
        title="Edit User"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmitEdit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                {...register('firstName')}
                type="text"
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                {...register('lastName')}
                type="text"
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              {...register('email', { 
                validate: (value) => {
                  if (!value && !watchedPhone) {
                    return 'Either email or phone is required';
                  }
                  if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Please enter a valid email address';
                  }
                  return true;
                }
              })}
              type="email"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
           
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              {...register('phone', { 
                validate: (value) => {
                  if (!value && !watchedEmail) {
                    return 'Either email or phone is required';
                  }
                  return true;
                }
              })}
              type="tel"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Tours
            </label>
            <div
              ref={editTourListRef}
              onScroll={handleTourListScroll}
              className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50"
            >
              {tours.map((tour) => (
                <label key={tour.id} className="flex items-center space-x-2 py-2 hover:bg-white hover:shadow-sm rounded px-2 transition-colors">
                  <input
                    type="checkbox"
                    checked={watchedAssignedTours.includes(tour.id)}
                    onChange={(e) => handleTourToggle(tour.id, e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-primary focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-700 flex-1">
                    {tour.title} {tour.place && `(${tour.place})`}
                  </span>
                </label>
              ))}
              {tours.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No tours available</p>
              )}
              {isLoadingMoreTours && (
                <div className="flex items-center justify-center py-2 space-x-2">
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
              {!isLoadingMoreTours && tourPage >= tourTotalPages && tourTotalPages > 1 && (
                <p className="text-xs text-center text-gray-400 py-1">All tours loaded</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedUser(null);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Create New User"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmitCreate)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                {...register('firstName')}
                type="text"
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                {...register('lastName')}
                type="text"
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              {...register('email', { 
                validate: (value) => {
                  if (!value && !watchedPhone) {
                    return 'Either email or phone is required';
                  }
                  if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Please enter a valid email address';
                  }
                  return true;
                }
              })}
              type="email"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>
           
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              {...register('phone', { 
                validate: (value) => {
                  if (!value && !watchedEmail) {
                    return 'Either email or phone is required';
                  }
                  return true;
                }
              })}
              type="tel"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Tours
            </label>
            <div
              ref={createTourListRef}
              onScroll={handleTourListScroll}
              className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 bg-gray-50"
            >
              {tours.map((tour) => (
                <label key={tour.id} className="flex items-center space-x-2 py-2 hover:bg-white hover:shadow-sm rounded px-2 transition-colors">
                  <input
                    type="checkbox"
                    checked={watchedAssignedTours.includes(tour.id)}
                    onChange={(e) => handleTourToggle(tour.id, e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-primary focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-700 flex-1">
                    {tour.title} {tour.place && `(${tour.place})`}
                  </span>
                </label>
              ))}
              {tours.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No tours available</p>
              )}
              {isLoadingMoreTours && (
                <div className="flex items-center justify-center py-2 space-x-2">
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="h-3 w-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
              {!isLoadingMoreTours && tourPage >= tourTotalPages && tourTotalPages > 1 && (
                <p className="text-xs text-center text-gray-400 py-1">All tours loaded</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users; 